import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, FieldPath, FieldValues, get } from 'react-hook-form';
import addIcon from '@/assets/icons/add.svg';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import { useExistingDocument } from '../ExistingDocumentsContext';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import styles from './FormField.module.scss';

interface FileFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  accept?: string;
}

export function FileField<T extends FieldValues>({
  name,
  label,
  accept = 'image/*,application/pdf',
}: FileFieldProps<T>) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<T>();
  const trackField = useOpenFundFieldTracking();
  const error = get(errors, name);
  const existing = useExistingDocument(name as string);
  const fieldName = String(name);

  return (
    <div className={styles.fieldWrapper}>
      <label className={`body-sm-regular ${styles.label}`}>{label}</label>
      {existing?.uri && (
        <div className="flex items-center gap-1 mb-1">
          <a
            href={existing.uri}
            target="_blank"
            rel="noopener noreferrer"
            className="body-sm-link text-(--font-link) block truncate"
          >
            {t('openFundRequest.edit.currentFile')}: {existing.name || t('openFundRequest.edit.viewFile')}
          </a>
          <DownloadFileButton uri={existing.uri} fileName={existing.name} />
        </div>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const file = field.value as File | null | undefined;
          return (
            <div
              className={`flex items-center justify-end gap-4 h-11 px-4 py-2 rounded-md ${
                file
                  ? 'border border-(--border-disabled) bg-(--surface-base)'
                  : 'bg-(--surface-grey-low-2)'
              } ${error ? '!border-(--font-error)' : ''}`}
            >
              <input
                id={name}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => {
                  trackField(fieldName);
                  const selected = e.target.files?.[0] ?? null;
                  if (selected && selected.size === 0) {
                    field.onChange(null);
                    setError(name, {
                      type: 'manual',
                      message: t('emptyFileSelected', { name: selected.name }),
                    });
                    e.target.value = '';
                    return;
                  }
                  clearErrors(name);
                  field.onChange(selected);
                }}
                aria-invalid={!!error}
              />
              {file ? (
                <div className="flex items-center gap-2 flex-1">
                  <button
                    type="button"
                    className="w-4 h-4 flex items-center justify-center text-(--font-body-3)"
                    onClick={() => field.onChange(null)}
                  >
                    ×
                  </button>
                  <span className="body-sm-regular text-(--font-body-1) truncate flex-1">
                    {file.name}
                  </span>
                </div>
              ) : (
                <span className="body-md-regular flex-1 text-right text-(--font-body-3)">
                  {label}
                </span>
              )}
              <label
                htmlFor={name}
                className="h-7 px-2 rounded border border-dashed flex items-center gap-1.5 body-sm-semibold bg-(--surface-action) border-(--border-primary) text-(--font-primary) cursor-pointer hover:bg-(--surface-primary-lowest)"
              >
                {existing?.uri
                  ? t('openFundRequest.buttons.replaceFile')
                  : t('openFundRequest.buttons.uploadFile')}
                <img src={addIcon} alt="" />
              </label>
            </div>
          );
        }}
      />
      {error && (
        <span className={`body-sm-regular ${styles.errorMessage}`}>
          {error.message as ReactNode}
        </span>
      )}
    </div>
  );
}
